// src/admin/pages/PromoCodesPage.js
import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  AlertCircle,
  Copy,
  Calendar,
  Percent,
  DollarSign,
  Gift
} from 'lucide-react';
import { supabase } from '../../config';

const PromoCodesPage = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Form state for creating/editing promo codes
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage', // percentage, fixed_amount, free_period
    value: '',
    max_uses: '',
    expires_at: '',
    description: '',
    is_active: true,
    applicable_plans: [],
    minimum_amount: '',
    first_time_only: false
  });

  // Mock data for now - replace with API calls
  const mockPromoCodes = [
    {
      id: 1,
      code: 'WELCOME20',
      type: 'percentage',
      value: 20,
      max_uses: 100,
      used_count: 45,
      expires_at: '2024-12-31',
      description: 'Welcome discount for new users',
      is_active: true,
      applicable_plans: ['basic', 'pro'],
      minimum_amount: 0,
      first_time_only: true,
      created_at: '2024-01-15'
    },
    {
      id: 2,
      code: '30DAYFREE',
      type: 'free_period',
      value: 30,
      max_uses: 50,
      used_count: 12,
      expires_at: '2024-06-30',
      description: '30-day free trial extension',
      is_active: true,
      applicable_plans: ['basic', 'pro'],
      minimum_amount: 0,
      first_time_only: false,
      created_at: '2024-01-10'
    },
    {
      id: 3,
      code: 'SAVE50',
      type: 'fixed_amount',
      value: 5000, // $50.00 in cents
      max_uses: 25,
      used_count: 8,
      expires_at: '2024-03-31',
      description: 'Save $50 on annual plans',
      is_active: true,
      applicable_plans: ['pro', 'enterprise'],
      minimum_amount: 10000, // $100 minimum
      first_time_only: false,
      created_at: '2024-01-05'
    },
    {
      id: 4,
      code: 'STUDENT10',
      type: 'percentage',
      value: 10,
      max_uses: 200,
      used_count: 150,
      expires_at: '2024-12-31',
      description: 'Student discount',
      is_active: false,
      applicable_plans: ['basic'],
      minimum_amount: 0,
      first_time_only: false,
      created_at: '2023-12-01'
    }
  ];

  const availablePlans = [
    { value: 'free', label: 'Free' },
    { value: 'basic', label: 'Basic' },
    { value: 'pro', label: 'Pro' },
    { value: 'enterprise', label: 'Enterprise' }
  ];

  useEffect(() => {
    fetchPromoCodes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      
      // Fetch from database
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching promo codes:', error);
        // Fallback to mock data if table doesn't exist
        setPromoCodes(mockPromoCodes);
      } else if (data && data.length > 0) {
        // Transform database data to match expected format
        const transformedCodes = data.map(code => ({
          id: code.id,
          code: code.code,
          type: code.type || 'percentage',
          value: code.value || 0,
          max_uses: code.max_uses || 0,
          used_count: code.used_count || 0,
          expires_at: code.expires_at || code.expires_at,
          description: code.description || '',
          is_active: code.is_active !== false,
          applicable_plans: code.applicable_plans || [],
          minimum_amount: code.minimum_amount || 0,
          first_time_only: code.first_time_only || false,
          created_at: code.created_at
        }));
        setPromoCodes(transformedCodes);
      } else {
        // No codes in database, use mock data
        setPromoCodes(mockPromoCodes);
      }
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      setPromoCodes(mockPromoCodes);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePlanToggle = (planValue) => {
    setFormData(prev => ({
      ...prev,
      applicable_plans: prev.applicable_plans.includes(planValue)
        ? prev.applicable_plans.filter(plan => plan !== planValue)
        : [...prev.applicable_plans, planValue]
    }));
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, code: result }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Format date properly - ensure it's in YYYY-MM-DD format or null
      let expiresAt = null;
      if (formData.expires_at && formData.expires_at.trim()) {
        // If already in YYYY-MM-DD format, use as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(formData.expires_at.trim())) {
          expiresAt = formData.expires_at.trim();
        } else {
          // Try to parse and format
          const date = new Date(formData.expires_at);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            expiresAt = `${year}-${month}-${day}`;
          }
        }
      }

      const codeData = {
        ...formData,
        value: parseFloat(formData.value) || 0,
        max_uses: parseInt(formData.max_uses) || 0,
        minimum_amount: parseInt(formData.minimum_amount) || 0,
        code: formData.code.toUpperCase(),
        expires_at: expiresAt
      };

      if (editingCode) {
        // Update existing code
        const { data, error } = await supabase
          .from('promo_codes')
          .update({
            code: codeData.code,
            type: codeData.type,
            value: codeData.value,
            max_uses: codeData.max_uses,
            expires_at: codeData.expires_at,
            description: codeData.description,
            is_active: codeData.is_active,
            applicable_plans: codeData.applicable_plans,
            minimum_amount: codeData.minimum_amount,
            first_time_only: codeData.first_time_only,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingCode.id)
          .select();

        if (error) {
          console.error('Update error:', error);
          throw error;
        }

        if (!data || data.length === 0) {
          throw new Error('No rows were updated. Please check if the promo code exists.');
        }

        setMessage('Promo code updated successfully!');
        await fetchPromoCodes();
      } else {
        // Create new code
        const { error } = await supabase
          .from('promo_codes')
          .insert({
            code: codeData.code,
            type: codeData.type,
            value: codeData.value,
            max_uses: codeData.max_uses,
            used_count: 0,
            expires_at: codeData.expires_at,
            description: codeData.description,
            is_active: codeData.is_active,
            applicable_plans: codeData.applicable_plans,
            minimum_amount: codeData.minimum_amount,
            first_time_only: codeData.first_time_only
          })
          .select();

        if (error) {
          throw error;
        }

        setMessage('Promo code created successfully!');
        await fetchPromoCodes();
      }

      setShowCreateForm(false);
      setEditingCode(null);
      resetForm();
    } catch (err) {
      setError(err.message || 'An error occurred while saving the promo code.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percentage',
      value: '',
      max_uses: '',
      expires_at: '',
      description: '',
      is_active: true,
      applicable_plans: [],
      minimum_amount: '',
      first_time_only: false
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // Otherwise, parse and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const handleEdit = (code) => {
    setFormData({
      code: code.code || '',
      type: code.type || 'percentage',
      value: code.value !== undefined ? String(code.value) : '',
      max_uses: code.max_uses !== undefined ? String(code.max_uses) : '',
      expires_at: formatDateForInput(code.expires_at),
      description: code.description || '',
      is_active: code.is_active !== undefined ? code.is_active : true,
      applicable_plans: code.applicable_plans || [],
      minimum_amount: code.minimum_amount !== undefined ? String(code.minimum_amount) : '',
      first_time_only: code.first_time_only || false
    });
    setEditingCode(code);
    setShowCreateForm(true);
  };

  const handleDelete = async (codeId) => {
    if (window.confirm('Are you sure you want to delete this promo code?')) {
      try {
        const { error } = await supabase
          .from('promo_codes')
          .delete()
          .eq('id', codeId);

        if (error) {
          throw error;
        }

        setMessage('Promo code deleted successfully!');
        await fetchPromoCodes();
      } catch (err) {
        setError(err.message || 'Failed to delete promo code.');
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('Code copied to clipboard!');
    setTimeout(() => setMessage(''), 2000);
  };

  const formatValue = (type, value) => {
    switch (type) {
      case 'percentage':
        return `${value}%`;
      case 'fixed_amount':
        return `$${(value / 100).toFixed(2)}`;
      case 'free_period':
        return `${value} days free`;
      default:
        return value;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'percentage': return Percent;
      case 'fixed_amount': return DollarSign;
      case 'free_period': return Gift;
      default: return Tag;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'percentage': return 'text-green-600';
      case 'fixed_amount': return 'text-blue-600';
      case 'free_period': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Promo Codes</h1>
          <p className="text-gray-600 mt-2">Create and manage promotional codes and discounts</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingCode(null);
            setShowCreateForm(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Promo Code
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <CheckCircle className="h-4 w-4 mr-2" />
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}

      {/* Promo Codes Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto w-full max-w-full">
          <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plans
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promoCodes.map((code) => {
                const TypeIcon = getTypeIcon(code.type);
                const expired = isExpired(code.expires_at);
                return (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {code.code}
                        </span>
                        <button
                          onClick={() => copyToClipboard(code.code)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {code.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <TypeIcon className={`h-4 w-4 mr-2 ${getTypeColor(code.type)}`} />
                        <span className="text-sm font-medium text-gray-900">
                          {formatValue(code.type, code.value)}
                        </span>
                      </div>
                      {code.minimum_amount > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          Min: ${(code.minimum_amount / 100).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {code.used_count} / {code.max_uses === 0 ? '∞' : code.max_uses}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ 
                            width: `${code.max_uses === 0 ? 0 : Math.min((code.used_count / code.max_uses) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        <span className={expired ? 'text-red-600' : 'text-gray-900'}>
                          {(() => {
                            try {
                              const date = new Date(code.expires_at);
                              if (isNaN(date.getTime())) return 'N/A';
                              const day = date.getDate();
                              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              const month = monthNames[date.getMonth()];
                              const year = date.getFullYear();
                              return `${day} ${month} ${year}`;
                            } catch {
                              return 'N/A';
                            }
                          })()}
                        </span>
                      </div>
                      {expired && (
                        <div className="text-xs text-red-600 mt-1">Expired</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {code.applicable_plans.map((plan) => (
                          <span 
                            key={plan}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {plan}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        code.is_active && !expired
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {code.is_active && !expired ? 'Active' : 'Inactive'}
                      </span>
                      {code.first_time_only && (
                        <div className="text-xs text-blue-600 mt-1">First time only</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(code)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(code.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingCode ? 'Edit Promo Code' : 'Create New Promo Code'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingCode(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Promo Code
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        placeholder="WELCOME20"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={generateRandomCode}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        Generate
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="percentage">Percentage Discount</option>
                      <option value="fixed_amount">Fixed Amount Discount</option>
                      <option value="free_period">Free Period</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.type === 'percentage' ? 'Discount Percentage' : 
                       formData.type === 'fixed_amount' ? 'Discount Amount (cents)' : 
                       'Free Days'}
                    </label>
                    <input
                      type="number"
                      name="value"
                      value={formData.value}
                      onChange={handleInputChange}
                      min="0"
                      step={formData.type === 'percentage' ? '1' : '1'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Uses (0 = unlimited)
                    </label>
                    <input
                      type="number"
                      name="max_uses"
                      value={formData.max_uses}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expires At
                    </label>
                    <input
                      type="date"
                      name="expires_at"
                      value={formData.expires_at}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Amount (cents, 0 = no minimum)
                    </label>
                    <input
                      type="number"
                      name="minimum_amount"
                      value={formData.minimum_amount}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Applicable Plans
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {availablePlans.map((plan) => (
                      <label key={plan.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.applicable_plans.includes(plan.value)}
                          onChange={() => handlePlanToggle(plan.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{plan.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="first_time_only"
                      checked={formData.first_time_only}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">First time only</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingCode(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : editingCode ? 'Update Code' : 'Create Code'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodesPage;
